import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import InfoDisplay from "@/components/InfoDisplay";
import type { FlatRateAgreement, Applicant, Consumer } from "../types";
import Layout from "../../shared/components/Layout";
import { formatDate, formatMonth } from '@/utils/format';
import { RegistrationParams } from "../types";
import createRegistration from "../api/createRegistration";
import { useFlatRateContext } from "../../shared/providers/FlatRateProvider";
import { AddressFieldBox } from "@/components/AddressField";
import { PhoneFieldBox } from "@/components/PhoneField";
import OccupationLabels from "../../shared/types/OccupationLabels";
import SexLabels from "../../shared/types/SexLabels";
import getRegistration from "../api/getRegistration";

const RegistrationConfirm: React.FC = () => {
  const displayPlan = (plan: string, price: string): string => {
    if(plan === 'both') return `両眼プラン(${price}円/月)`
    if(plan === 'right') return `片眼プラン(右眼)(${price}円/月)`
    if(plan === 'left') return `片眼プラン(左眼)(${price}円/月)`
    return '';
  }

  const OccupationLabels: OccupationLabels = {
    employee: "会社員・公務員",
    independent: "自営",
    housewife: "主婦",
    student: "学生",
    other: "その他"
  }

  const SexLabels: SexLabels = {
    male: "男性",
    female: "女性",
    unknown: "不明",
  }

  const navigate = useNavigate();

  const { passwordValues, setAlert } = useFlatRateContext();

  const [customer, setCustomer] = useState({
    custCode: "",
    email: "",
  });

  const [flatRateAgreement, setFlatRateAgreement] = useState<FlatRateAgreement>();
  const [applicant, setApplicant] = useState<Applicant>({
    address1: "",
    address2: "",
    birthday: "",
    homePhone: "",
    mobilePhone: "",
    name: "",
    nameKana: "",
    occupation: "",
    sex: "",
    stateId: "",
    zipcode: "",
  });
  const [consumer, setConsumer] = useState<Consumer>({
    address1: "",
    address2: "",
    birthday: "",
    homePhone: "",
    mobilePhone: "",
    name: "",
    nameKana: "",
    occupation: "",
    relationship: "",
    sex: "",
    stateId: "",
    zipcode: "",
    isSameApplicant: true,
    isSameAddress: true,
    isSamePhone: true
  });
  
  const [states, setStates] = useState([]);
  const [gmoUrl, setGmoUrl] = useState<string>("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRegistration();
        if (response.data.flatRateAgreement.status !== "before_confirmation") navigate("/customer/top");
        const storedCustomer = localStorage.getItem("customer");
        if(storedCustomer) {
          setCustomer(JSON.parse(storedCustomer));
        }

        const storedApplicant = localStorage.getItem("applicant");
        if(storedApplicant) {
          setApplicant(JSON.parse(storedApplicant));
        }

        const storedConsumer = localStorage.getItem("consumer");
        if(storedConsumer) {
          setConsumer(JSON.parse(storedConsumer));
        }

        const storedFlatRateAgreement = localStorage.getItem("flatRateAgreement");
        if(storedFlatRateAgreement) {
          setFlatRateAgreement(JSON.parse(storedFlatRateAgreement));
        }
    
        const storedStates = localStorage.getItem("states");
        if(storedStates) {
          setStates(JSON.parse(storedStates));
        }

        const storedGmoUrl = localStorage.getItem("gmoUrl");
        if(storedGmoUrl) {
          setGmoUrl(JSON.parse(storedGmoUrl));
        }

      } catch (error) {
        console.error("Error fetching flat rate lens data:", error);
      }
    };

    fetchData();
  }, [navigate]);

  const applicantAddressData = {
    zipcode: {
      label: "郵便番号",
      value: applicant.zipcode,
      onChange: undefined
    },
    stateId: {
      label: "都道府県",
      value: applicant.stateId,
      onChange: undefined
    },
    address1: {
      label: "市区町村",
      placeholder: "",
      value: applicant.address1,
      onChange: undefined
    },
    address2: {
      label: "建物名",
      placeholder: "",
      value: applicant.address2,
      onChange: undefined
    },
  }

  const consumerAddressData = {
    zipcode: {
      label: "郵便番号",
      value: consumer.zipcode,
      onChange: undefined
    },
    stateId: {
      label: "都道府県",
      value: consumer.stateId,
      onChange: undefined
    },
    address1: {
      label: "市区町村",
      placeholder: "",
      value: consumer.address1,
      onChange: undefined
    },
    address2: {
      label: "建物名",
      placeholder: "",
      value: consumer.address2,
      onChange: undefined
    },
  }

  const handleSubmit = async() => {

    const params: RegistrationParams = {
      customer: {...customer, ...passwordValues},
      applicant: applicant,
      consumer: consumer,
    }

    try {
      await createRegistration(params);
    } catch (err: any) {
      console.log('An unexpected error occurred:', err.message);
    }
  }

  const renderPreviousPage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigate("/customer/flat_rates/registration", { state: { fromConfirmPage: true } });
    window.scrollTo(0, 0);
  }

  const passwordValidate = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setAlert({open: true, messages: ['パスワードを再入力してください。']});
    navigate("/customer/flat_rates/registration");
    window.scrollTo(0, 0);
  }

  return(
    <Layout>
      <main id="page-user_index" className="wrapper">
        <section className="sec-contact">
          <div className="inner">
            <h1 className="c-ttl__01">新規契約申込確認</h1>
            <div className="contact-box">
              <div className="contact-box__left">
                <div className="contact-box__form js-item" id="id-01">
                  <h2 className="c-ttl__02">お客様情報</h2>
                  <InfoDisplay label={"お客様ID"} value={customer.custCode} />
                  <InfoDisplay label={"契約プラン"} value={displayPlan(flatRateAgreement?.plan ?? '', flatRateAgreement?.price?.toString() ?? '')} />
                  <InfoDisplay label={"サービス利用開始日"} value={formatDate(flatRateAgreement?.startDate)} />
                  <InfoDisplay label="サービス利用開始月" value={formatMonth(flatRateAgreement?.startMonth ?? '')} />
                  <InfoDisplay label={"メールアドレス"} value={customer.email} />
                  <InfoDisplay label={"パスワード"} value={passwordValues.password ? "*********" : ""} />
                  <InfoDisplay label={"パスワード確認"} value={passwordValues.passwordConfirmation ? "*********" : ""} />
                </div>
                <div className="contact-box__form mbsp-29 js-item h-adr" id="id-02">
                  <h2 className="c-ttl__02">申込者情報</h2>
                  <InfoDisplay label="申込者名(カナ)" value={applicant.nameKana} />
                  <InfoDisplay label="申込者名(漢字)" value={applicant.name} />
                  <InfoDisplay label="申込者性別" value={applicant.sex ? SexLabels[applicant.sex] : ""} />
                  <InfoDisplay label="申込者生年月日" value={formatDate(applicant.birthday)} />
                  <AddressFieldBox
                    label="申込者住所"
                    addressData={applicantAddressData}
                    required={false}
                    states={states}
                    inputAddressData={undefined}
                  />
                  <PhoneFieldBox
                    label="申込者電話番号"
                    phoneData={{
                      homePhone: {label: "自宅", value: applicant.homePhone, onChange: undefined},
                      mobilePhone: {label: "携帯", value: applicant.mobilePhone, onChange: undefined},
                    }}
                    required={false}
                  />
                  <InfoDisplay label="申込者ご職業" value={applicant.occupation ? OccupationLabels[applicant.occupation] : ""} />
                </div>
                <div className="contact-box__form mbsp-29 js-item h-adr" id="id-03">
                  <h2 className="c-ttl__02">レンズ使用者情報</h2>
                  <InfoDisplay label="使用者について" value={consumer.isSameApplicant ? "申込者と同一" : "申込者と異なる"} />
                  { !consumer.isSameApplicant &&
                    <>
                      <InfoDisplay label="使用者名(カナ)" value={consumer.nameKana} />
                      <InfoDisplay label="使用者名(漢字)" value={consumer.name} />
                      <InfoDisplay label="申込者との関係" value={consumer.relationship} />
                      <InfoDisplay label="使用者性別" value={consumer.sex ? SexLabels[consumer.sex] : ""} />
                      <InfoDisplay label="使用者生年月日" value={formatDate(consumer.birthday)} />
                      { consumer.isSameAddress ?
                        <InfoDisplay label="使用者住所" value={"申込者と同一"} />
                      :
                        <AddressFieldBox
                          label="使用者住所"
                          addressData={consumerAddressData}
                          required={false}
                          states={states}
                          inputAddressData={undefined}
                        />
                      }

                      { consumer.isSamePhone ?
                        <InfoDisplay label="使用者電話番号" value={"申込者と同一"} />
                      :
                        <PhoneFieldBox
                          label="使用者電話番号"
                          phoneData={{
                            homePhone: {label: "自宅", value: consumer.homePhone, onChange: undefined},
                            mobilePhone: {label: "携帯", value: consumer.mobilePhone, onChange: undefined},
                          }}
                          required={false}
                        />
                      }
                      <InfoDisplay label="申込者ご職業" value={consumer.occupation ? OccupationLabels[consumer.occupation] : ""} />
                    </>
                  }
                </div>
                {(passwordValues.password === '' && passwordValues.passwordConfirmation === '') ?
                  <button className="c-btn__01" onClick={passwordValidate}>確定</button>
                  :
                  <a className="c-btn__01" href={gmoUrl} onClick={handleSubmit}>確定</a>
                }
                
                <div className="c-al--c">
                  <button className="c-btn__02" onClick={renderPreviousPage}>前のページに戻る</button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  )
};

export default RegistrationConfirm;